import { inject, Injectable } from '@angular/core';
import {
  preOrderLocale,
  reOrderLocale,
} from '@epicuro-next/constants/features/localization';
import {
  IFilterValue,
  ReportingApiService,
} from '@epicuro-next/data/reporting-api';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, tap } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import {
  BestPerformingArticleQuery,
  BestPerformingArticlesState,
} from '../../models';

const initialState: BestPerformingArticlesState = {
  bestPerformingArticles: [],
  filters: null,
  params: {
    topN: 10,
    brandOrgIds: [],
    categories: [],
    orderType: [],
  },
  hasError: false,
  loading: true,
};

const orderTypeFilterValues: IFilterValue[] = [
  { key: '0', value: reOrderLocale },
  { key: '1', value: preOrderLocale },
];

@Injectable()
export class BestPerformingArticlesStore extends ComponentStore<BestPerformingArticlesState> {
  constructor() {
    super(initialState);
    this.loadFilters();
  }

  private reportingService = inject(ReportingApiService);
  private params$ = this.select(({ params }) => params);

  public bestPerformingArticles$ = this.select(
    (state) => state.bestPerformingArticles,
  );
  public filters$ = this.select((state) => state.filters);
  public hasError$ = this.select((state) => state.hasError);
  public loading$ = this.select((state) => state.loading);

  public hasFiltersSelected$ = this.select(
    this.params$,
    (query) =>
      (query?.brandOrgIds && query.brandOrgIds.length > 0) ||
      (query?.orderType && query.orderType.length > 0) ||
      (query?.categories && query.categories.length > 0),
  );

  public selectedBrandOrg$ = this.select(
    this.params$,
    (query) => query?.brandOrgIds,
  );

  public selectedOrderType$ = this.select(
    this.params$,
    (query) => query?.orderType,
  );

  public selectedCategories$ = this.select(
    this.params$,
    (query) => query?.categories,
  );

  public selectedSort$ = this.select(
    this.params$,
    (query) => query?.columnSort,
  );

  public readonly loadTopArticles = this.effect(
    (params$: Observable<BestPerformingArticleQuery>) =>
      params$.pipe(
        tap(() => this.patchState({ loading: true })),
        switchMap(
          ({
            topN,
            dateRange = { from: '', to: '' },
            brandOrgIds,
            categories,
            orderType,
            columnSort,
          }) =>
            this.reportingService
              .getTopArticles(
                topN ?? 0,
                dateRange,
                brandOrgIds,
                categories,
                orderType?.[0],
                columnSort,
              )
              .pipe(
                tapResponse(
                  (topArticles) => {
                    this.patchState({
                      bestPerformingArticles: topArticles.topProducts,
                      hasError: false,
                      loading: false,
                    });
                  },
                  () => this.patchState({ loading: false, hasError: true }),
                  () => this.patchState({ loading: false, hasError: false }),
                ),
              ),
        ),
      ),
  )(this.params$);

  public loadFilters = this.effect((void$: Observable<void>) =>
    void$.pipe(
      switchMap(() =>
        this.reportingService.getTopArticleFilters().pipe(
          tapResponse(
            (filterResponse) => {
              const filters = {
                ...filterResponse,
                orderTypes: orderTypeFilterValues,
              };
              this.patchState({ filters });
            },
            () => this.patchState({ hasError: true }),
          ),
        ),
      ),
    ),
  );

  public setParams(payload: Partial<BestPerformingArticleQuery>) {
    this.patchState((state) => ({
      params: {
        topN: 10,
        ...state.params,
        ...payload,
      },
    }));
  }
}
